import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from '@atoms/new/modal';
import { Tab, Tabs } from '@atoms/new/tab';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import { format } from 'date-fns';
import WorkspaceName from './WorkspaceName';
import InviteMember from './InviteMember';
import { useQuery } from '@tanstack/react-query';
import {
  getTeamByIdServiceSide,
  removeTeamMember,
} from '@services/youtube-platform.service';
import { getAuth } from '@services/identity.service';
import CellData from './CellData';
import { notify } from '@components/Presentation/helpers';
import { EnvelopeSimpleIcon, UsersIcon } from '@phosphor-icons/react';

interface WorkspaceProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange: (isOpen: boolean) => void;
}

const activeColumns = [
  {
    key: 'member',
    label: 'Member',
  },
  {
    key: 'date',
    label: 'Join Date',
  },
  {
    key: 'role',
    label: 'Role',
  },
];
const pendingColumns = [
  {
    key: 'member',
    label: 'Email',
  },
  {
    key: 'date',
    label: 'Last Sent',
  },
  {
    key: 'role',
    label: 'Revoke',
  },
];

export default function Workspace({
  isOpen,
  onClose,
  onOpenChange,
}: WorkspaceProps) {
  const [selectedKey, setSelectedKey] = useState('active');
  const columns = selectedKey === 'active' ? activeColumns : pendingColumns;
  const { ownTeam, currentTeam, updateTeam, setTeamMessages } = useTeamsData((s) => ({ ownTeam: s.ownTeam, currentTeam: s.currentTeam, updateTeam: s.updateTeam, setTeamMessages: s.setTeamMessages }));

  // const owner = filterTeamsByRole('owner')?.[0];

  const { data: team, refetch } = useQuery({
    queryKey: ['current-team-query', currentTeam?.billing_account.id],
    queryFn: () =>
      getTeamByIdServiceSide(currentTeam!.billing_account.id, getAuth()),
    enabled: !!currentTeam,
  });

  const getRoleName = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Admin';
      default:
        return 'Editor & Moderator';
    }
  };

  type Team = {
    key: string;
    member: string;
    date: string;
    role: string;
  };
  let activeMembers: Team[] = [];
  let pendingMembers: Team[] = [];
  team?.entity?.members?.forEach((member: any) => {
    if (member.accepted_on) {
      activeMembers.push({
        key: member.id,
        member: member.email,
        date: format(new Date(member.accepted_on), 'MMM dd, yyyy, h:mm a'),
        role: getRoleName(member.role),
      });
    } else {
      pendingMembers.push({
        key: member.id,
        member: member.email,
        date: format(new Date(member.createdAt), 'MMM dd, yyyy, h:mm a'),
        role: 'Pending',
      });
    }
  });

  const rows = selectedKey === 'active' ? activeMembers : pendingMembers;

  const classNames = useMemo(
    () => ({
      wrapper: ['max-w-3xl', 'shadow-none', 'px-0', 'bg-transparent'],
      th: ['bg-transparent', 'text-default-500', 'border-b', 'border-divider'],
      td: [
        // changing the rows border radius
        // first
        'group-data-[first=true]:first:before:rounded-none',
        'group-data-[first=true]:last:before:rounded-none',
        // middle
        'group-data-[middle=true]:before:rounded-none',
        // last
        'group-data-[last=true]:first:before:rounded-none',
        'group-data-[last=true]:last:before:rounded-none',
        'border-b border-grey-200 dark:border-grey-900',
      ],
    }),
    []
  );

  const deleteItem = async (id: string) => {
    if (!team) return;
    const response = await removeTeamMember(id, team.entity.id);
    if (response.status) {
      updateTeam(response.entity);
      // onSuccess(response.entity);
      setTeamMessages([response.message]);
      notify(response.message, 'success', { position: 'top-center' });
      refetch();
      // onCancel();
    } else {
      // setErrors([response.message]);
      console.error(response.message);
    }
  };
  const isOwner =
    currentTeam?.billing_account?.owner_id ===
    ownTeam?.billing_account?.owner_id;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpenChange={onOpenChange}
      className="w-[800px]"
    >
      <ModalContent>
        <ModalHeader className="mb-6">Workspace settings</ModalHeader>
        <ModalBody>
          <WorkspaceName
            adminTeam={team?.entity}
            onSuccess={refetch}
            isOwner={isOwner}
          />
          <InviteMember
            team={team?.entity}
            onSuccess={refetch}
            isOwner={isOwner}
            onClose={onClose}
          />
          <div className="mt-6 flex">
            <Tabs
              aria-label=""
              selectedKey={selectedKey}
              onSelectionChange={(k) => setSelectedKey(k as string)}
            >
              <Tab
                key="active"
                title={
                  <div className="flex items-center">
                    <UsersIcon size={20} className="mr-2" />
                    Active members
                    <span className="ml-1">{`(${activeMembers.length})`}</span>
                  </div>
                }
              />
              <Tab
                key="invite"
                title={
                  <div className="flex items-center">
                    <EnvelopeSimpleIcon size={20} className="mr-2" />
                    Invitations
                    <span className="ml-1">{`(${pendingMembers.length})`}</span>
                  </div>
                }
              />
            </Tabs>
          </div>
          {!rows.length && (
            <div className="h-[100px] bg-grey-50 dark:bg-grey-800 grid place-items-center mt-4 font-medium text-grey-800 dark:text-grey-200">
              No pending invites.
            </div>
          )}
          {!!rows.length && (
            <Table aria-label="Members" classNames={classNames}>
              <TableHeader columns={columns} className="border-0 bg-white">
                {(column) => (
                  <TableColumn
                    key={column.key}
                    className=" text-sm text-grey-700 dark:text-grey-500"
                  >
                    {column.label}
                  </TableColumn>
                )}
              </TableHeader>

              <TableBody items={rows}>
                {(item) => (
                  <TableRow key={item.key}>
                    {(columnKey) => (
                      <TableCell className="h-12">
                        <CellData
                          deleteItem={deleteItem}
                          columnKey={columnKey}
                          item={item}
                          isOwner={isOwner}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
